import { useMemo, useState, useEffect } from "react";
import { useQuery, QueryKey } from "@tanstack/react-query";
import { queryAPI } from "../api/queryDetails";

import { Pagination } from "../../../shared/types";
import { getIds } from "../../../shared/utils/getIds";

export const useQueryListing = ({
    page,
    limit,
    searchKey,
    category,
    priority,
    status,
    user_id,
    project_id,
    project_tree_id,
    portfolio_id,
    programme_id,
}: {
    page: number;
    limit: number;
    searchKey: string | null;
    category: string | null;
    priority: string | null;
    status: string | null;
    user_id: string | null;
    project_id: any;
    project_tree_id: string | null;
    portfolio_id: any;
    programme_id: any;
}) => {

    const [pagination, setPagination] = useState<Pagination>({
        has_next: false,
        has_previous: false,
        limit: 10,
        page: 1,
        total: 0,
        total_pages: 1,
    });

    // convert objects → stable keys
    const stableProjectId = useMemo(() => JSON.stringify(getIds(project_id)), [project_id]);
    const stablePortfolioId = useMemo(() => JSON.stringify(getIds(portfolio_id)), [portfolio_id]);
    const stableProgrammeId = useMemo(() => JSON.stringify(getIds(programme_id)), [programme_id]);

    /** State query */
    const stateQuery = useQuery({
        queryKey: [
            "query-listing-states",
            searchKey,
            category,
            priority,
            status,
            user_id,
            stableProjectId,
            project_tree_id,
            stablePortfolioId,
            stableProgrammeId,
        ] as QueryKey,
        queryFn: () =>
            queryAPI.fetchStates({
                params: {
                    search_term: searchKey,
                    category,
                    priority,
                    status,
                    user_id,
                    project_id: getIds(project_id),
                    project_tree_id: getIds(project_tree_id),
                    portfolio_id: getIds(portfolio_id),
                    programme_id: getIds(programme_id),
                },
            }),
    });

    /** Main query */
    const mainQuery = useQuery({
        queryKey: [
            "query-listing",
            page,
            limit,
            searchKey,
            category,
            priority,
            status,
            user_id,
            stableProjectId,
            project_tree_id,
            stablePortfolioId,
            stableProgrammeId,
        ] as QueryKey,
        queryFn: () =>
            queryAPI.fetch({
                params: {
                    page,
                    limit,
                    search_term: searchKey,
                    category,
                    priority,
                    status,
                    user_id,
                    project_id: getIds(project_id),
                    project_tree_id: getIds(project_tree_id),
                    portfolio_id: getIds(portfolio_id),
                    programme_id: getIds(programme_id),
                },
            }),
    });

    /** Update pagination */
    useEffect(() => {
        const pagination = mainQuery?.data?.data?.pagination ?? {};
        setPagination({
            has_next: pagination.has_next ?? false,
            has_previous: pagination.has_previous ?? false,
            limit: pagination.limit ?? 10,
            page: pagination.page ?? 1,
            total: pagination.total ?? 0,
            total_pages: pagination.total_pages ?? 1,
        });
    }, [mainQuery.data]);

    return {
        pagination,
        isLoading: mainQuery.isLoading || stateQuery.isLoading,
        isFetching: mainQuery.isFetching || stateQuery.isFetching,
        data: mainQuery.data?.data?.rows || [],
        stateData: stateQuery.data?.data || [],
        refetch: mainQuery.refetch,
        refetchStates: stateQuery.refetch,
    };

};